import { TmBaseComponent } from '@treasury/presentation';
import { checkmarkIcon, closeIcon, lockIcon } from '@treasury/presentation/assets/icons';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import './jhd-icon';

export type DecisionState = 'Approved' | 'Rejected' | 'na';
export type JhdDecisionToggleChangeEvent = CustomEvent<{
    selection: DecisionState;
    modified: boolean;
}>;

/**
 * A switch that toggles between red and green circular icons.
 * User can toggle whether the component state has been 'modified' by clicking the original decision button.
 * User cannot set the decision to 'na' after render. This component's decision will always be 'Approved' or 'Rejected' unless none is provided.
 * @param {DecisionState} decision - the selected decision status
 * @param {DecisionState} originalState - Optional. For tracking orignal state outside of component scope.
 * @param {boolean} readonly - Optional.
 * @param {boolean} modified - Optional.
 * @param {boolean} protected - Optional.
 * If the 'protected' property is true, the toggle switch will render in a locked state.
 */
export const tagName = 'jhd-decision-toggle';
@customElement(tagName)
export class JhdDecisionToggle extends TmBaseComponent {
    @property()
    public decision: DecisionState = 'na';

    @property()
    public originalState?: DecisionState;

    @property({ type: Boolean, reflect: true })
    public readonly = false;

    @property({ type: Boolean })
    public modified?: boolean;

    @property({ type: Boolean })
    public protected?: boolean;

    @property({ type: Boolean })
    public filledBackground?: boolean;

    private readonly defaultColor = 'var(--content-default)';

    private readonly whiteColor = 'var(--content-icon-approval)';

    private readonly greyColor = 'var(--content-deselected)';

    private readonly greenColor = 'var(--content-positive)';

    private readonly redColor = 'var(--content-negative)';

    @state()
    private approveColor = this.defaultColor;

    @state()
    private rejectColor = this.defaultColor;

    protected updated(changedProps: Map<string, unknown>) {
        super.updated(changedProps);
        if (changedProps.has('decision')) {
            this.determineIconColor();
        }
    }

    protected render() {
        return html`<div class="justify-around py-2 pr-3 flex-0">${this.renderActions()}</div>`;
    }

    private renderIcon(decision: DecisionState) {
        this.determineIconColor();

        const isApproveIcon = decision === 'Approved';
        const approved = this.decision === 'Approved';
        const rejected = this.decision === 'Rejected';
        const decisionNa = this.decision === 'na';
        const deselected = isApproveIcon ? rejected : approved;
        const backgroundFilledIn = !!this.modified || !!this.filledBackground;
        const approvedBg = isApproveIcon && backgroundFilledIn && approved;
        const rejectedBg = !isApproveIcon && backgroundFilledIn && rejected;
        const iconWrapperClass = `icon-wrapper-${decision.toLowerCase()}`;
        const iconColor = isApproveIcon ? this.approveColor : this.rejectColor;

        const icon = html`
            <div
                class="${classMap({
                    'icon-wrapper': true,
                    'w-5 h-5': true,
                    'border rounded-full': true,
                    'flex justify-center items-center': true,
                    [iconWrapperClass]: true,
                    'icon-wrapper-deselected': deselected,
                    'icon-wrapper-na': decisionNa,
                    'dirty-approved': !!approvedBg,
                    'dirty-rejected': !!rejectedBg,
                })}"
            >
                <jhd-icon
                    .icon=${isApproveIcon ? checkmarkIcon : closeIcon}
                    .color=${iconColor}
                    .width=${'16px'}
                    .height=${'16px'}
                ></jhd-icon>
            </div>
        `;

        if (this.protected) {
            if (decision !== this.decision) {
                return html`
                    <div class="flex justify-center items-center w-5 h-5 icon-wrapper">
                        <jhd-icon
                            .icon=${lockIcon}
                            .color=${iconColor}
                            .width=${'16px'}
                            .height=${'16px'}
                        ></jhd-icon>
                    </div>
                `;
            }

            return html`
                <div class="flex justify-center items-center w-10 h-10">
                    <div
                        class="${classMap({
                            'icon-wrapper': true,
                            'w-5 h-5': true,
                            'border-2 rounded-full': true,
                            'flex justify-center items-center': true,
                            [iconWrapperClass]: true,
                            'icon-wrapper-deselected': isApproveIcon ? rejected : approved,
                            'dirty-approved': isApproveIcon,
                            'dirty-rejected': !isApproveIcon,
                            'opacity-50': true,
                        })}"
                    >
                        <jhd-icon
                            .icon=${isApproveIcon ? checkmarkIcon : closeIcon}
                            .color=${this.whiteColor}
                            .width=${'16px'}
                            .height=${'16px'}
                        ></jhd-icon>
                    </div>
                </div>
            `;
        }

        return this.readonly
            ? html`<div class="flex justify-center items-center w-10 h-10">${icon}</div>`
            : icon;
    }

    private renderButton(decision: 'Approved' | 'Rejected') {
        const buttonClass = `${decision.toLocaleLowerCase()}-button`;

        if (this.protected) {
            return html`
                <div class="flex justify-center items-center w-10 h-10 ${buttonClass}">
                    ${this.renderIcon(decision)}
                </div>
            `;
        }

        return html`<button
            class="flex justify-center items-center w-10 h-10 ${buttonClass}"
            @click=${() => {
                if (decision === this.originalState && decision === this.decision) {
                    this.modified = !this.modified;
                } else {
                    this.modified = true;
                }
                this.decision = decision;
                this.determineIconColor();
                this.dispatchEvent(
                    new CustomEvent('toggle-change', {
                        detail: { selection: this.decision, modified: this.modified },
                    })
                );
            }}
        >
            ${this.renderIcon(decision)}
        </button>`;
    }

    private renderActions() {
        if (this.readonly) {
            if (this.decision === 'na') {
                return nothing;
            }
            return html`${this.renderIcon(this.decision)}`;
        }
        return html`${this.renderButton('Approved')} ${this.renderButton('Rejected')}`;
    }

    private determineIconColor() {
        if (this.modified || this.filledBackground) {
            if (this.decision === 'Approved') {
                this.approveColor = this.whiteColor;
                this.rejectColor = this.greyColor;
            }
            if (this.decision === 'Rejected') {
                this.approveColor = this.greyColor;
                this.rejectColor = this.whiteColor;
            }
        } else {
            if (this.decision === 'Approved') {
                this.approveColor = this.greenColor;
                this.rejectColor = this.greyColor;
            }
            if (this.decision === 'Rejected') {
                this.approveColor = this.greyColor;
                this.rejectColor = this.redColor;
            }
        }
        if (this.decision === 'na') {
            this.approveColor = this.defaultColor;
            this.rejectColor = this.defaultColor;
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    --content-icon-approval: var(--jh-color-gray-0);
                }
                :host([theme='dark']) {
                    --content-icon-approval: var(--brand-500);
                }
                div {
                    display: flex;
                }
                .hide {
                    display: none;
                }
                button {
                    border: none;
                }
                .icon-wrapper {
                    padding: 2px;
                    border-color: var(--content-default);
                }
                .icon-wrapper-approved {
                    border-color: var(--content-positive);
                }
                .icon-wrapper-rejected {
                    border-color: var(--content-negative);
                }
                .icon-wrapper-deselected {
                    border-color: var(--content-deselected);
                }
                .icon-wrapper-na {
                    border-color: var(--content-default);
                }
                jhd-icon {
                    position: relative;
                }
                .dirty-approved {
                    background-color: var(--content-positive);
                }

                .dirty-rejected {
                    background-color: var(--content-negative);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: JhdDecisionToggle;
    }
}
